import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'semantic-ui-react';
import { addDocumentToGroup } from '../../services/Documents';
import sendToast from '../../../utils/Toast';
import { DocumentFilterOption } from '../../types/DocumentFilters';
import { DOC_TYPE_ICON, docTypeFromFileName, formatFileSize } from '../../utils/document';
import { UploadIcon, CloseIcon } from '../icons';

export const MY_DOCS_VALUE = 'mydocs';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

interface Props {
    open: boolean;
    facilityId: string;
    residentId: string;
    groupOptions: DocumentFilterOption[];
    onClose: () => void;
    onUploaded: (targetGroupId: string) => void;
}

const UploadDocumentModal = ({ open, facilityId, residentId, groupOptions, onClose, onUploaded }: Props) => {
    const [file, setFile] = useState<File | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [targetGroupId, setTargetGroupId] = useState(MY_DOCS_VALUE);
    const [dragOver, setDragOver] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setFile(null);
            setName('');
            setDescription('');
            setTargetGroupId(MY_DOCS_VALUE);
            setDragOver(false);
        }
    }, [open]);

    const handleFileSelect = (files: FileList | null) => {
        const selected = files?.[0];
        if (!selected) return;

        if (selected.size > MAX_FILE_SIZE) {
            sendToast('error', 'File must be under 100MB');
            // Reset the input so re-selecting the same (rejected) file fires onChange again —
            // a file input only fires change when its value actually changes, so without this
            // a retry on the identical file would silently do nothing.
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setFile(selected);
        setName((prev) => (prev.trim() ? prev : selected.name.replace(/\.[^.]+$/, '')));
    };

    const handleClearFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    const handleSubmit = async () => {
        if (!file) {
            sendToast('error', 'Please choose a file first');
            return;
        }
        const trimmedName = name.trim();
        if (!trimmedName) {
            sendToast('error', 'Please enter a document name');
            return;
        }

        try {
            setSubmitting(true);
            await addDocumentToGroup(facilityId, residentId, {
                Name: trimmedName,
                Description: description.trim(),
                file,
                groupId: targetGroupId === MY_DOCS_VALUE ? undefined : targetGroupId,
            });
            sendToast('success', `"${trimmedName}" uploaded`);
            onUploaded(targetGroupId);
            onClose();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error uploading document');
        } finally {
            setSubmitting(false);
        }
    };

    const fileType = file ? docTypeFromFileName(file.name) : 'default';
    const FileTypeIcon = DOC_TYPE_ICON[fileType];

    return (
        <Modal
            className="upload-modal"
            open={open}
            onClose={handleClose}
            closeOnDimmerClick={!submitting}
            closeOnEscape={!submitting}
            centered={false}
            dimmer="blurring"
            size="small"
        >
            <Modal.Header className="upload-modal-header">
                <div className="upload-modal-title">Upload a Document</div>
                <button type="button" className="upload-modal-close" onClick={handleClose} aria-label="Close" disabled={submitting}>
                    <CloseIcon size={18} />
                </button>
            </Modal.Header>

            <Modal.Content className="upload-modal-body" scrolling>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic"
                    style={{ display: 'none' }}
                    onChange={(event) => handleFileSelect(event.target.files)}
                />

                {!file ? (
                    <div
                        className={`upload-dropzone ${dragOver ? 'dragover' : ''}`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(event) => {
                            event.preventDefault();
                            setDragOver(false);
                            handleFileSelect(event.dataTransfer.files);
                        }}
                    >
                        <div className="upload-dropzone-icon"><UploadIcon size={34} strokeWidth={1.8} /></div>
                        <div className="upload-dropzone-text"><strong>Tap to choose a file</strong> or drag it here</div>
                        <div className="upload-dropzone-hint">PDF, Word document, or image</div>
                    </div>
                ) : (
                    <div className="upload-file-selected">
                        <div className={`doc-icon-wrap ${fileType}`}><FileTypeIcon /></div>
                        <div className="upload-file-info">
                            <div className="upload-file-name">{file.name}</div>
                            <div className="upload-file-size">{formatFileSize(file.size)}</div>
                        </div>
                        <button type="button" className="upload-file-change" onClick={handleClearFile}>Change</button>
                    </div>
                )}

                <div className="upload-field">
                    <label htmlFor="docNameInput">Document name</label>
                    <input
                        id="docNameInput"
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="e.g. My Care Preferences"
                    />
                </div>

                <div className="upload-field">
                    <label htmlFor="docDescInput">Description <span className="upload-field-optional">(optional)</span></label>
                    <textarea
                        id="docDescInput"
                        rows={2}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="A short note about this document"
                    />
                </div>

                <div className="upload-field">
                    <label htmlFor="docGroupInput">Save to</label>
                    <select id="docGroupInput" value={targetGroupId} onChange={(event) => setTargetGroupId(event.target.value)}>
                        <option value={MY_DOCS_VALUE}>My Docs (private to me)</option>
                        {groupOptions.map((group) => (
                            <option key={group.key} value={group.value}>{group.text}</option>
                        ))}
                    </select>
                </div>
            </Modal.Content>

            <Modal.Actions className="upload-modal-footer">
                <Button basic className="upload-btn-secondary" onClick={handleClose} disabled={submitting}>Cancel</Button>
                <Button primary className="upload-btn-primary" onClick={handleSubmit} loading={submitting} disabled={submitting}>Upload</Button>
            </Modal.Actions>
        </Modal>
    );
};

export default UploadDocumentModal;
